import { useRef, useState } from "react";
import { FolderUp, CheckCircle2, AlertTriangle, Loader2, MinusCircle } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money, monthLabel } from "../lib/format";
import { Panel } from "./Widgets";
import { Button } from "./ui/button";

const ICON = {
  done: <CheckCircle2 size={14} className="text-emerald-400" />,
  skipped: <MinusCircle size={14} className="text-slate-400" />,
  error: <AlertTriangle size={14} className="text-amber-400" />,
  working: <Loader2 size={14} className="text-blue-300 animate-spin" />,
  queued: <span className="w-3.5 h-3.5 rounded-full border border-slate-600 inline-block" />,
};

async function importOne(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data: p } = await api.post("/uploads/preview", fd);
  if (!p.matched_preset) return { status: "error", text: "Format not recognised — import it with the single upload above" };
  if (!p.detected_month) return { status: "error", text: "No report month found in the sheet" };
  const body = new FormData();
  body.append("file", file); body.append("month", p.detected_month); body.append("mapping", JSON.stringify(p.suggested_mapping));
  body.append("source", p.matched_preset); body.append("data_start_row", p.data_start_row);
  try {
    const { data } = await api.post("/uploads/ingest", body);
    return { status: "done", month: p.detected_month, text: <>{data.row_count} rows · <span>{money(data.total_residual)}</span> residual · <span>{money(data.total_payout)}</span> payouts</> };
  } catch (e) {
    if (e.response?.status === 409) return { status: "skipped", month: p.detected_month, text: "Already imported for this month" };
    throw e;
  }
}

export function BulkUpload() {
  const ref = useRef();
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const update = (i, patch) => setJobs((js) => js.map((j, k) => (k === i ? { ...j, ...patch } : j)));

  const run = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setJobs(list.map((f, i) => ({ id: `${i}-${f.name}-${f.lastModified}`, name: f.name, status: "queued", text: "" })));
    setBusy(true);
    for (let i = 0; i < list.length; i++) {
      update(i, { status: "working", text: "Importing…" });
      try { update(i, await importOne(list[i])); } catch (e) { update(i, { status: "error", text: errMsg(e) }); }
    }
    setBusy(false);
    ref.current.value = "";
  };

  const done = jobs.filter((j) => j.status === "done").length;
  return (
    <Panel title="Historical uploads" className="mt-6" action={<span className="text-xs text-slate-500">import many past months at once</span>}>
      <div className="p-5" data-testid="bulk-upload">
        <p className="text-sm text-slate-400 mb-4">Select several monthly reports in a recognised format (e.g. Merchant Detail Horizontal). Each file's month is read from its date column, and files already imported are skipped.</p>
        <input ref={ref} type="file" multiple accept=".xlsx,.xls,.csv" className="hidden" data-testid="bulk-file-input" onChange={(e) => run(e.target.files)} />
        <Button data-testid="bulk-upload-button" variant="outline" disabled={busy} onClick={() => ref.current.click()}>
          <FolderUp size={14} className="mr-2" />{busy ? "Importing…" : "Choose report files"}
        </Button>
        {jobs.length > 0 && (
          <div className="mt-4 divide-y divide-slate-800 border border-slate-800 rounded-md" data-testid="bulk-results">
            {jobs.map((j, i) => (
              <div key={j.id} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`bulk-job-${i}`}>
                {ICON[j.status]}
                <span className="truncate flex-1">{j.name}</span>
                {j.month && <span className="font-mono text-xs text-slate-400">{monthLabel(j.month)}</span>}
                <span className={`text-xs ${j.status === "error" ? "text-amber-300" : "text-slate-400"}`} data-testid={`bulk-job-status-${i}`}>{j.text}</span>
              </div>
            ))}
            {!busy && <div className="px-4 py-2.5 text-xs text-slate-500">{done} of {jobs.length} imported</div>}
          </div>
        )}
      </div>
    </Panel>
  );
}
